import './App.less';
// 必须引，否则没有css变量
import '@bfe/moly/dist/es/theme/cssVars/index.css';
// 必须引 否则组件没有样式
import '@bfe/moly/dist/es/theme/styles/tailwind.css';
import '@bfe/moly/dist/es/theme/font/index.css';

import { ConfigProvider, message } from 'antd';
import { useEffect, useLayoutEffect } from 'react';
import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { isOAuthHeader, NO_HEADER_H5_PATH } from '@/utils/constants';
import { $tracing } from '@/utils/tracing';
import isApp, { isMobile } from '@/utils/ua';

import SecuritySettingModalManager from './components/SecuritySettingModalManager';
import { useApp } from './hooks/useApp';
import { useAppAdapter } from './hooks/useAppAdapter';
import { useLogout } from './hooks/useLogout';
import { useUserProfile } from './hooks/useUserProfile';
import { I18nProvider } from './i18n/I18n';
import { i18nCache } from './i18n/url';
import store from './store';
import { DeadPoolProvider } from './store/useDeadPool';
import { SecuritySettingModalProvider } from './store/useSecuritySettingModal/useSecuritySettingModal';
import { SecuritySettingModalDataProvider } from './store/useSecuritySettingModal/useSecuritySettingModalData';
import { GlobalProvider } from './store/useStore';
import { UserProvider } from './store/useUser';
import { adapterH5, appLayoutAdapter } from './utils/appLayoutAdapter';
import { demoAccountAllowPath } from './utils/constants/common';
import { reactRTLJsInit, rtlAdapter } from './utils/rtl';

interface IUniframe {
  header: any;
  footer: any;
  security: any;
  transferAsset: any;
  transferAccountConfig: any;
  adaBot: any;
  securityItem: any;
}
reactRTLJsInit(React);
const ScrollToTop = (props: any) => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return props.children;
};
function App() {
  const { locale, setAntdLocale, supportLang } = useApp();
  const {
    userInfo,
    loading: userInfoLoading,
    mutate,
    ...userRole
  } = useUserProfile();

  const logout = useLogout();
  useAppAdapter();
  useEffect(() => {
    rtlAdapter();

    if (isMobile()) {
      const el = document.createElement('meta');
      el.setAttribute('name', 'viewport');
      el.setAttribute(
        'content',
        'width=device-width,initial-scale=0.85,user-scalable=no',
      );
      document.head.append(el);
    }
  }, []);

  useEffect(() => {
    const path = window.location.pathname;
    if (
      !userInfoLoading &&
      userRole?.isDemoAccount &&
      !demoAccountAllowPath.includes(path)
    ) {
      message.info({
        content: 'Demo accounts cannot access this page',
        onClose() {
          window.location.href = window.location.origin;
        },
      });
    }
  }, [userRole?.isDemoAccount, userInfoLoading]);

  useLayoutEffect(() => {
    if (!window.BybitUniFrame) return;

    const handleLangChange = (lang: string) => {
      let antL = lang;
      //TODO: en-US -> en
      // if (!lang || lang === 'en-US') {
      if (!lang) {
        antL = 'en';
      }
      console.log('LANGUAGE_CHANGE', antL, supportLang(antL));
      if (window.localStorage && supportLang(antL)) {
        localStorage.setItem('BYBIT_LANG_KEY', antL);
      }
      if (
        /^\/[a-z]{2,3}(-[A-Z]{2,3})?\/(notifications|user-feedback|unsubscribe|notifications-setting)[/]?$/.test(
          window.location.pathname,
        )
      ) {
        const pathName = window.location.pathname.replace(
          /^\/[a-z]{2,3}(-[A-Z]{2,3})?/,
          '',
        );
        window.location.href = `/${antL}${pathName}${window.location.search}${window.location.hash}`;
      }
      i18nCache.i18n.changeLanguage(antL);
      setAntdLocale(antL);
      // rtl布局
      rtlAdapter();
    };

    const handleLogout = () => {
      $tracing.addEvent({ t: 'action', c: 10024 });
      logout();
    };

    window.BybitUniFrame?.ready(
      ({
        header,
        footer,
        // transferAsset,
        // transferAccountConfig,
        adaBot,
        security,
        securityItem,
      }: IUniframe) => {
        const { USER_INFO } = header.properties;
        const {
          USER_INFO: ADA_USER_INFO,
          LANG,
          HIDE_PAGE_URL,
        } = adaBot
          ? adaBot.properties
          : {
              USER_INFO: 'ADA_USER_INFO',
              LANG: 'LANG',
              HIDE_PAGE_URL: 'HIDE_PAGE_URL',
            };

        if (
          (!isMobile() ||
            NO_HEADER_H5_PATH.every(
              (pathname: string) => location.pathname !== pathname,
            )) &&
          !isOAuthHeader()
        ) {
          console.log(isMobile(), 'isMobile()');

          header
            .init({
              containerId: '__bybit_uniframe_header__',
            })
            .then(() => {
              const { LOGOUT, LANGUAGE_CHANGE } = header.events;
              let IS_HOME;
              if (header.properties) {
                IS_HOME = header.properties.IS_HOME;
              }

              header
                .setProperty(IS_HOME, true)
                .subscribeEvent(LOGOUT, () => {
                  handleLogout();
                })
                .subscribeEvent(LANGUAGE_CHANGE, (lang: string) => {
                  // const la = lang === 'en-US' ? 'en' : lang;

                  handleLangChange(lang);
                  adaBot && adaBot.setProperty(LANG, lang);
                });
              // header加载后隐藏
              adapterH5('header');
            });
          header.setProperty(USER_INFO, userInfo);

          setTimeout(() => {
            footer.init().then(() => {
              const { IS_GENERAL } = footer.properties;
              footer.setProperty(IS_GENERAL, true);
              isApp() && appLayoutAdapter();
            });

            // footer加载后隐藏
            adapterH5('footer');
          }, 4 * 1000);
        }

        // 初始化security
        setTimeout(() => {
          const { PRESTRAIN } = securityItem.properties;
          if (
            ['/app/user/security', '/app/user/account-info'].includes(
              window.location.pathname,
            )
          ) {
            securityItem.setProperty(PRESTRAIN, true);
          }
          security.init();
          adaBot &&
            adaBot.init().then(() => {
              adaBot.setProperty(ADA_USER_INFO, userInfo);
              adaBot.setProperty(HIDE_PAGE_URL, ['/app/user/trading-tool']);
            });
        }, 8000);
      },
    );
  }, [userInfo]);

  const AppContainer = (
    // eslint-disable-next-line react/no-unescaped-entities
    <ScrollToTop>
      <I18nProvider>
        <UserProvider>
          <DeadPoolProvider>
            <GlobalProvider
              value={{
                ...store.state,
                userInfo,
                userRole,
                userInfoLoading,
                mutateUserInfo: mutate,
              }}
            >
              <SecuritySettingModalDataProvider>
                <SecuritySettingModalProvider>
                  <ConfigProvider locale={locale}>
                    {/* Outlet 为独立路由页面 */}
                    <Outlet />
                    <SecuritySettingModalManager />
                  </ConfigProvider>
                </SecuritySettingModalProvider>
              </SecuritySettingModalDataProvider>
            </GlobalProvider>
          </DeadPoolProvider>
        </UserProvider>
      </I18nProvider>
    </ScrollToTop>
  );
  return AppContainer;
}

export default App;
